import { DataTable } from "@/components/data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { columns } from "../_misc/columns";
import {
  useGetCardTransactions,
  useGetDeposits,
  useGetDepositsInfinite,
  useHasSwaps,
} from "@/hooks/use-queries";
import TopBar from "@/components/topbar";
import { cardColumns } from "../_misc/cardColumns";
import TransactionList from "../_misc/TransactionList";
import SwapTab from "../_misc/swap/swap-tab";
import { useTabParam } from "@/hooks/use-tab-param";
import { useMediaQuery } from "@/hooks/use-media-query";

const PAGE_SIZE = 10;

type TransactionTab = "wallet" | "cards" | "swap";

const Transactions = () => {
  const hasSwaps = useHasSwaps();
  // Same breakpoint as the `lg:hidden` mobile list / `hidden lg:block` table.
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const [tab, setTab] = useTabParam<TransactionTab>(
    "tab",
    "wallet",
    hasSwaps ? ["wallet", "cards", "swap"] : ["wallet", "cards"],
  );

  // ── Wallet: server-paginated ────────────────────────────────────────────
  const [walletPage, setWalletPage] = useState(1);
  const { data: transactions, isLoading: isLoadingTransactions } =
    useGetDeposits({ page: walletPage, limit: PAGE_SIZE }, !isMobile);
  const walletRows: any[] = transactions?.data ?? [];
  const walletPageCount = Math.max(1, transactions?.totalPages || 1);

  // Mobile: the same endpoint, but pages accumulate as the user scrolls.
  const {
    data: walletInfinite,
    isLoading: isLoadingWalletInfinite,
    isFetchingNextPage: isFetchingMoreWallet,
    hasNextPage: hasMoreWallet,
    fetchNextPage: fetchMoreWallet,
  } = useGetDepositsInfinite(PAGE_SIZE, isMobile);
  const walletInfiniteRows: any[] =
    walletInfinite?.pages.flatMap((page) => page?.data ?? []) ?? [];

  // ── Cards: fetched in one batch, paginated on the client ────────────────
  const [cardPage, setCardPage] = useState(1);
  const { data: cardTransactions, isLoading: isLoadingCardTransactions } =
    useGetCardTransactions({ page: 0, limit: 150 });
  const cardRows: any[] = cardTransactions?.data ?? [];
  const cardPageCount = Math.max(1, Math.ceil(cardRows.length / PAGE_SIZE));
  const cardPageRows = cardRows.slice(
    (cardPage - 1) * PAGE_SIZE,
    cardPage * PAGE_SIZE,
  );
  // Mobile reveals the already-fetched list PAGE_SIZE at a time as it scrolls.
  const [cardVisibleCount, setCardVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Keep the active page in range if the row count shrinks under it. Guard on a
  // loaded response — while react-query refetches an uncached page, `data` is
  // briefly undefined and `totalPages` would read as 1, bouncing us to page 1.
  useEffect(() => {
    if (transactions?.totalPages) {
      setWalletPage((p) => Math.min(p, walletPageCount));
    }
  }, [transactions?.totalPages, walletPageCount]);
  useEffect(() => {
    setCardPage((p) => Math.min(p, cardPageCount));
  }, [cardPageCount]);

  // <DataTable> wants a { pageIndex, pageSize } object and a setter; bridge it
  // to a plain 1-indexed page number, clamping whatever tanstack's nav hands
  // back into range.
  const tableProps = (
    page: number,
    setPage: (p: number) => void,
    pageCount: number,
  ) => ({
    pageCount,
    pagination: { pageIndex: page, pageSize: PAGE_SIZE },
    setPagination: (updater: unknown) => {
      const prev = { pageIndex: page, pageSize: PAGE_SIZE };
      const next =
        typeof updater === "function"
          ? (updater as (p: typeof prev) => typeof prev)(prev)
          : (updater as typeof prev);
      const target = next?.pageIndex ?? page;
      setPage(Math.min(Math.max(1, target), Math.max(1, pageCount)));
    },
  });

  return (
    <div className="">
      <TopBar title="Transactions" />
      <Tabs
        onValueChange={(value) => setTab(value as TransactionTab)}
        value={tab}
        className="font-poppins px-4 py-4 lg:bg-transparent"
      >
        <TabsList className="border-dark-stroke-3 bg-dark-background-light w-full border p-0 lg:w-fit">
          <TabsTrigger
            value="wallet"
            className="text-dark-text-300 data-[state=active]:bg-dark-primary-50 data-[state=active]:text-dark-text-400 rounded-l-lg rounded-r-none px-6"
          >
            Wallet
          </TabsTrigger>
          <TabsTrigger
            value="cards"
            className={`text-dark-text-300 data-[state=active]:bg-dark-primary-50 data-[state=active]:text-dark-text-400 rounded-l-none px-6 ${hasSwaps ? "rounded-r-none" : "rounded-r-lg"}`}
          >
            Cards
          </TabsTrigger>
          {hasSwaps && (
            <TabsTrigger
              value="swap"
              className="text-dark-text-300 data-[state=active]:bg-dark-primary-50 data-[state=active]:text-dark-text-400 rounded-l-none rounded-r-lg px-6"
            >
              Swap
            </TabsTrigger>
          )}
        </TabsList>
        <div className="bg-dark-card-3 text-dark-text-400 mt-4 rounded-2xl p-4 backdrop-blur-sm lg:row-start-auto">
          <div className="flex items-center justify-between bg-white/5"></div>

          <TabsContent value="wallet" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={columns}
                data={walletRows}
                isLoading={isLoadingTransactions}
                {...tableProps(walletPage, setWalletPage, walletPageCount)}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingWalletInfinite}
                transactions={walletInfiniteRows}
                hasMore={hasMoreWallet}
                isLoadingMore={isFetchingMoreWallet}
                onLoadMore={() => fetchMoreWallet()}
              />
            </div>
          </TabsContent>

          <TabsContent value="cards" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={cardColumns}
                data={cardPageRows}
                isLoading={isLoadingCardTransactions}
                {...tableProps(cardPage, setCardPage, cardPageCount)}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingCardTransactions}
                transactions={cardRows.slice(0, cardVisibleCount)}
                type="cards"
                hasMore={cardVisibleCount < cardRows.length}
                onLoadMore={() =>
                  setCardVisibleCount((count) => count + PAGE_SIZE)
                }
              />
            </div>
          </TabsContent>

          {hasSwaps && (
            <TabsContent value="swap" className="mt-2">
              <SwapTab />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
};

export default Transactions;
